// ============================================================
// Moteur PUR de l'ORDRE DES LIGNES d'un emploi du temps — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-roster.spec.ts.
// ============================================================
//
// L'ordre alphabétique n'est pas celui d'un planning. Un gérant range ses lignes comme il range
// son équipe : les deux personnes qui se relaient l'une sous l'autre, l'apprenti sous son tuteur,
// le renfort du samedi en bas. Il faut donc que cet ordre-là soit une DONNÉE, qu'il se déplace à
// la main et qu'il survive au rechargement.
//
// OÙ IL VIT : `hr_schedule.data.member_seq`, un objet `{ "<id_employee>": rang }`. Dans le JSONB
// de la grille, pas dans une colonne — aucun schéma serveur à toucher, et l'ordre voyage avec le
// planning qu'il décrit. Les rangs sont réécrits en 1, 2, 3… à chaque déplacement : on ne cherche
// jamais à insérer « entre 2 et 3 ».
//
// QUI N'Y FIGURE PAS : une personne ajoutée depuis un autre écran, ou dont on vient de cocher le
// nom, n'a pas encore de rang. Elle ne disparaît pas et ne remonte pas en tête : elle se range
// APRÈS celles qui en ont un, dans l'ordre de secours (les éligibles d'abord, puis l'alphabet).

/// Les rangs enregistrés, lus défensivement : le JSONB vient de la base, il peut contenir
/// n'importe quoi (une reprise de données, une version antérieure, une main humaine).
export function rosterSeqOf(data: any): Map<number, number> {
	const out = new Map<number, number>();
	const raw = data?.member_seq;
	if (!raw || typeof raw !== 'object') return out;
	for (const [key, value] of Object.entries(raw)) {
		const id = +key, seq = +(value as any);
		if (!isNaN(id) && id > 0 && !isNaN(seq)) out.set(id, seq);
	}
	return out;
}

/// L'ordre d'affichage des lignes : d'abord les rangs enregistrés, puis le reste selon l'ordre de
/// secours. `fallback` reçoit les ids sans rang et les rend triés (l'écran y met « éligibles
/// d'abord, puis alphabétique » — le tri qui existait avant que l'ordre soit une donnée).
export function rosterOrderOf(
	ids: number[],
	data: any,
	fallback: (rest: number[]) => number[] = rest => rest,
): number[] {
	const seq = rosterSeqOf(data);
	const ranked = ids.filter(id => seq.has(id)).sort((a, b) => seq.get(a)! - seq.get(b)! || a - b);
	return [...ranked, ...fallback(ids.filter(id => !seq.has(id)))];
}

/// Le `member_seq` à enregistrer pour un ordre donné — renuméroté 1, 2, 3…
export function rosterSeqData(order: number[]): Record<string, number> {
	const out: Record<string, number> = {};
	order.forEach((id, i) => { out['' + id] = i + 1; });
	return out;
}

/// Le `data` de la grille, enrichi de l'ordre. Le reste du JSONB est CONSERVÉ : on ne sait pas ce
/// que d'autres écrans y auront rangé, et l'écraser serait leur faire perdre leurs réglages.
export function withRosterSeq(data: any, order: number[]): any {
	return { ...(data && typeof data === 'object' ? data : {}), member_seq: rosterSeqData(order) };
}

/// Un comparateur d'ordre prêt pour `Array.sort`, à partir d'une liste d'ids ordonnée.
/// Les inconnus vont à la fin plutôt qu'au début : une ligne qui apparaît ne bouscule personne.
export function rosterCompare(order: number[]): (a: number, b: number) => number {
	const rank = new Map(order.map((id, i) => [id, i]));
	return (a, b) => (rank.get(a) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b) ?? Number.MAX_SAFE_INTEGER);
}

// ============================================================
// Déplacer une ligne dans une liste FILTRÉE
// ============================================================
//
// Le planning n'affiche jamais tout le monde : un magasin est choisi, une équipe, et les autres
// sont hors écran. Déplacer une ligne doit alors changer la place des seules personnes VISIBLES et
// laisser les autres exactement où elles sont — sinon ranger son équipe d'Ollioules rebattrait en
// douce l'ordre de La Valette, que l'on n'a même pas sous les yeux.
//
// Le procédé : on relève les PLACES qu'occupaient les visibles dans l'ordre complet, on réordonne
// les visibles entre eux, puis on les redistribue dans ces mêmes places. Les invisibles ne bougent
// pas d'un cran, et l'ordre général reste un seul et même classement.

/// L'ordre complet après déplacement de la ligne `from` en position `to` DANS LA LISTE VISIBLE.
/// `order` est l'ordre complet (les visibles en sont un sous-ensemble, dans le même ordre relatif) ;
/// les visibles absents de `order` sont ajoutés à la fin avant le déplacement.
export function rosterMoveVisible(order: number[], visible: number[], from: number, to: number): number[] {
	const full = [...order, ...visible.filter(id => !order.includes(id))];
	if (from === to || from < 0 || to < 0 || from >= visible.length || to >= visible.length) return full;

	const moved = [...visible];
	moved.splice(to, 0, ...moved.splice(from, 1));

	const shown = new Set(visible);
	const seats: number[] = [];
	full.forEach((id, i) => { if (shown.has(id)) seats.push(i); });

	const out = [...full];
	seats.forEach((seat, k) => { out[seat] = moved[k]; });
	return out;
}

/// L'ensemble des ids à classer : ceux que l'écran connaît, PLUS ceux qui ont déjà un rang
/// enregistré. Sans eux, enregistrer l'ordre depuis un écran filtré effacerait le rang de tous
/// les absents — leur place serait perdue au premier déplacement fait magasin par magasin.
export function rosterUniverse(ids: number[], data: any): number[] {
	return [...new Set([...ids, ...rosterSeqOf(data).keys()])];
}
